import { FC } from 'react';
import { Modal, RadioGroup, TextArea, Checkbox, CodeDisplay, Button } from '@aidkitorg/component-library';
import { safeParse } from '../../../../../../Util';
import { useLocalizedStrings } from '../../../../../../Localization';
import { DebugMessageModalProps, HideMessageModalProps, DeleteAttachmentsModalProps, DeleteRecordingModalProps, DeleteMessageModalProps } from '../shared/types';
import { getHideMessageReasonOptions } from '../shared/constants';

// Debug Message Modal
export const DebugMessageModal: FC<DebugMessageModalProps> = ({ showDebugMessage, onClose }) => {
  const L = useLocalizedStrings();

  if (!showDebugMessage) return null;

  return (
    <Modal size="lg" title={L.applicant.comms.debug_message} open={!!showDebugMessage} onClose={onClose}>
      <div className="max-h-[70vh] overflow-auto">
        <CodeDisplay
          code={JSON.stringify({
            ...showDebugMessage,
            message: showDebugMessage.message ? {
              ...showDebugMessage.message,
              metadata: safeParse(showDebugMessage.message.metadata || '{}')
            } : undefined
          }, null, 2)}
          language="json"
          expandable={true}
          copyable={true}
        />
      </div>
    </Modal>
  );
};

// Hide Message Modal
export const HideMessageModal: FC<HideMessageModalProps> = ({
  showHideMessage,
  hideMessageReason,
  hideMessageDetails,
  shouldHideAttachmentOnly,
  onClose,
  onReasonChange,
  onDetailsChange,
  onAttachmentOnlyChange,
  onConfirm,
}) => {
  const L = useLocalizedStrings();

  if (!showHideMessage) return null;

  const hideMessageReasonOptions = getHideMessageReasonOptions(L);

  return (
    <Modal title={L.applicant.comms.hide_message} open={!!showHideMessage} onClose={onClose}>
      <div className="space-y-4">
        <RadioGroup
          label={L.applicant.comms.hide_message_reason}
          options={hideMessageReasonOptions}
          value={hideMessageReason}
          onChange={(value) => onReasonChange(value as any)}
        />

        <TextArea
          label={L.applicant.comms.hide_message_details}
          placeholder={L.applicant.comms.hide_message_details}
          value={hideMessageDetails}
          onChange={(e) => onDetailsChange(e.target.value)}
        />

        {safeParse(showHideMessage.message?.metadata || '{}').attachments?.length > 0 && (
          <Checkbox
            label={L.applicant.comms.hide_attachment_only}
            checked={shouldHideAttachmentOnly}
            description={shouldHideAttachmentOnly ? L.applicant.comms.hide_attachment_only_description : undefined}
            onChange={onAttachmentOnlyChange}
          />
        )}

        <div className="flex justify-end gap-x-3">
          <Button size="md" variant="secondary" onClick={onClose}>
            {L.apply.cancel}
          </Button>
          <Button
            size="md"
            variant="primary"
            isDisabled={!hideMessageReason}
            onClick={onConfirm}
          >
            {L.applicant.comms.hide_message}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// Delete Attachments Modal
export const DeleteAttachmentsModal: FC<DeleteAttachmentsModalProps> = ({
  showDeleteAttachments,
  deleteAttachmentsReason,
  onClose,
  onReasonChange,
  onConfirm,
}) => {
  const L = useLocalizedStrings();

  if (!showDeleteAttachments) return null;

  return (
    <Modal title={L.applicant.comms.delete_attachments} open={showDeleteAttachments} onClose={onClose}>
      <div className="space-y-4">
        <p>{L.applicant.comms.delete_attachments_description}</p>
        <TextArea
          label={L.applicant.comms.delete_artifact_reason}
          placeholder={L.applicant.comms.delete_artifact_reason}
          value={deleteAttachmentsReason}
          onChange={(e) => onReasonChange(e.target.value)}
          required
        />

        <div className="flex justify-end gap-x-3">
          <Button size="md" variant="secondary" onClick={onClose}>
            {L.apply.cancel}
          </Button>
          <Button
            size="md"
            variant="primary"
            isDisabled={!deleteAttachmentsReason.trim()}
            onClick={onConfirm}
          >
            {L.applicant.comms.delete_attachments}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// Delete Recording Modal
export const DeleteRecordingModal: FC<DeleteRecordingModalProps> = ({
  showDeleteRecording,
  deleteRecordingReason,
  onClose,
  onReasonChange,
  onConfirm,
}) => {
  const L = useLocalizedStrings();

  if (!showDeleteRecording) return null;

  return (
    <Modal title={L.applicant.comms.delete_recording} open={showDeleteRecording} onClose={onClose}>
      <div className="space-y-4">
        <p>{L.applicant.comms.delete_recording_description}</p>
        <TextArea
          label={L.applicant.comms.delete_artifact_reason}
          placeholder={L.applicant.comms.delete_artifact_reason}
          value={deleteRecordingReason}
          onChange={(e) => onReasonChange(e.target.value)}
          required
        />

        <div className="flex justify-end gap-x-3">
          <Button size="md" variant="secondary" onClick={onClose}>
            {L.apply.cancel}
          </Button>
          <Button
            size="md"
            variant="primary"
            isDisabled={!deleteRecordingReason.trim()}
            onClick={onConfirm}
          >
            {L.applicant.comms.delete_recording}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// Delete Message Modal
export const DeleteMessageModal: FC<DeleteMessageModalProps> = ({
  isOpen,
  deleteMessageReason,
  onClose,
  onReasonChange,
  onConfirm,
}) => {
  const L = useLocalizedStrings();

  if (!isOpen) return null;

  return (
    <Modal title={L.applicant.comms.delete_message} open={isOpen} onClose={onClose}>
      <div className="space-y-4">
        <p>{L.applicant.comms.delete_message_description}</p>
        <TextArea
          label={L.applicant.comms.delete_artifact_reason}
          placeholder={L.applicant.comms.delete_artifact_reason}
          value={deleteMessageReason}
          onChange={(e) => onReasonChange(e.target.value)}
          required
        />

        <div className="flex justify-end gap-x-3">
          <Button size="md" variant="secondary" onClick={onClose}>
            {L.apply.cancel}
          </Button>
          <Button
            size="md"
            variant="primary"
            isDisabled={!deleteMessageReason.trim()}
            onClick={onConfirm}
          >
            {L.applicant.comms.delete_message}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
